import { Link } from "react-router-dom";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { Search, Briefcase, UserPlus, ArrowRight } from "lucide-react";
import EmployerConversionBanner from "../components/EmployerConversionBanner";

export default function PublicHome() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      <main>
        <section className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-400 mb-3">People. Skills. Business. Connected.™</div>
            <h1 className="font-display font-bold text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl">Don't ask who to hire.<br/><span className="text-teal-300">Ask what work needs to get done.</span></h1>
            <p className="text-slate-300 text-lg mt-6 max-w-2xl">ConnectMyHub™ is a global workforce marketplace by My Pipeline Solutions — direct hire, managed workforce, and AI-augmented teams.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/jobs" data-testid="hero-find-jobs" className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100">
                <Search size={16}/> Find jobs <ArrowRight size={14}/>
              </Link>
              <Link to="/pricing" data-testid="hero-for-employers" className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-slate-700 text-white font-semibold text-sm hover:bg-slate-800">
                <Briefcase size={16}/> For employers
              </Link>
            </div>
          </div>
        </section>

        <EmployerConversionBanner />

        <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-8">
          <div className="p-6 rounded-xl border border-slate-200 bg-slate-50" data-testid="talent-card">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600 mb-2">Talent / Job seekers</div>
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">I'm looking for work</h2>
            <p className="text-sm text-slate-600 mb-4">Browse opportunities for free. Create a free candidate account when you're ready to apply. Candidates never pay to apply, be matched, interview, or get hired through the core ConnectMyHub platform.</p>
            <div className="flex flex-wrap gap-2">
              <Link to="/jobs" data-testid="talent-find-jobs" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-slate-900 text-white text-xs font-semibold">Find jobs <ArrowRight size={12}/></Link>
              <Link to="/portal/signup" data-testid="talent-create-account" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-300 text-slate-800 text-xs font-semibold"><UserPlus size={12}/> Create free candidate account</Link>
            </div>
          </div>
          <div className="p-6 rounded-xl border border-slate-200 bg-white" data-testid="employer-card">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-700 mb-2">Employers / Companies hiring</div>
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">I'm hiring</h2>
            <p className="text-sm text-slate-600 mb-4">Hire globally with placement-guaranteed Direct Hire. Free to create an employer account — purchase a membership when you're ready.</p>
            <div className="flex flex-wrap gap-2">
              <Link to="/pricing" data-testid="employer-pricing" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-slate-900 text-white text-xs font-semibold">See pricing</Link>
              <Link to="/employer-portal/signup" data-testid="employer-create-account" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-300 text-slate-800 text-xs font-semibold">Create employer account</Link>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="rounded-2xl bg-slate-950 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="home-ai-workforce-cta">
            <div className="max-w-2xl">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-300 mb-2">AI Workforce · Hire an AI Employee</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl">HIRE YOUR AI WORKFORCE</h2>
              <p className="text-sm text-slate-300 mt-3">Hire specialized AI Employees to help your business sell, market, operate and grow. Build your AI workforce with specialized AI Employees and AI Departments on a simple monthly membership.</p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Link to="/workforce" data-testid="home-browse-ai-employees" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-md bg-teal-500 text-slate-950 text-xs font-semibold uppercase tracking-widest hover:bg-teal-400">Browse AI Employees <ArrowRight size={12}/></Link>
              <Link to="/workforce#wf-departments" data-testid="home-build-ai-workforce" className="inline-flex items-center gap-1.5 h-11 px-5 rounded-md border border-slate-600 text-white text-xs font-semibold uppercase tracking-widest hover:border-teal-400">Build My AI Workforce</Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter/>
    </div>
  );
}

export function PublicNav() {
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center"><InfinityMark size={20}/></div>
          <div className="font-display font-bold text-slate-900">Connect<span className="text-teal-600">My</span>Hub<span className="text-teal-600 text-xs">™</span></div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-700">
          <Link to="/" data-testid="nav-home" className="hover:text-slate-900">Home</Link>
          <Link to="/jobs" data-testid="nav-find-jobs" className="hover:text-sky-600">Find jobs</Link>
          <Link to="/workforce" data-testid="nav-ai-workforce" title="Hire AI Employees" className="hover:text-teal-700 inline-flex flex-col leading-tight"><span>AI Workforce</span><span className="text-[9px] font-normal uppercase tracking-widest text-teal-600">Hire AI Employees</span></Link>
          <Link to="/pricing" data-testid="nav-for-employers" className="hover:text-slate-900">For employers</Link>
          <Link to="/advertise" data-testid="nav-advertise" className="hover:text-slate-900">Advertise</Link>
          <Link to="/login" data-testid="nav-sign-in" className="h-9 inline-flex items-center px-4 rounded-md bg-slate-900 text-white text-xs">Sign in</Link>
        </nav>
        <div className="md:hidden flex items-center gap-2">
          <Link to="/jobs" data-testid="nav-find-jobs-mobile" className="text-xs font-semibold text-sky-600">Find jobs</Link>
          <Link to="/workforce" data-testid="nav-ai-workforce-mobile" className="text-xs font-semibold text-teal-700">AI Workforce</Link>
          <Link to="/login" data-testid="nav-sign-in-mobile" className="h-9 inline-flex items-center px-3 rounded-md bg-slate-900 text-white text-xs">Sign in</Link>
        </div>
      </div>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-3">
        <div>© 2026 My Pipeline Solutions. ConnectMyHub™ — People. Skills. Business. Connected.™</div>
        <div className="flex gap-4">
          <Link to="/jobs" className="hover:text-slate-800">Find jobs</Link>
          <Link to="/workforce" data-testid="footer-workforce" className="hover:text-slate-800">Workforce</Link>
          <Link to="/pricing" className="hover:text-slate-800">For employers</Link>
          <Link to="/advertise" data-testid="footer-advertise" className="hover:text-slate-800">Advertise</Link>
          <Link to="/login" className="hover:text-slate-800">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}
